import React, { useRef, useCallback } from "react";

import { Box, Button, Typography, TableRow, TableCell } from "@mui/material";

// RecessionButton component with long-press support for negative values (gingival overgrowth)
// Uses Pointer Events API for unified mouse/touch/pen handling
// Supports drag: captures pointer on press, releases capture if pointer moves >8px so the container can take over
const RecessionButton = ({ val, currentValue, onClick, onLongPress, disabled, isLargeScreen, toothNum, row }) => {
    const timerRef = useRef(null);
    const isLongPress = useRef(false);
    const isPressed = useRef(false);
    const startPos = useRef(null);
    const isDragged = useRef(false);

    const cleanup = useCallback(() => {
        isPressed.current = false;
        isLongPress.current = false;
        isDragged.current = false;
        startPos.current = null;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const handlePointerDown = useCallback(
        (e) => {
            if (disabled) return;

            // Prevent default to avoid synthetic click events on mobile
            e.preventDefault();
            // Capture pointer; will be released if drag detected (>8px)
            e.target.setPointerCapture(e.pointerId);

            isPressed.current = true;
            isLongPress.current = false;
            isDragged.current = false;
            startPos.current = { x: e.clientX, y: e.clientY };

            // Only enable long-press for values 1-5
            if (val <= 5) {
                timerRef.current = setTimeout(() => {
                    if (!isDragged.current) {
                        isLongPress.current = true;
                        onLongPress();
                    }
                }, 350);
            }
        },
        [disabled, val, onLongPress]
    );

    const handlePointerMove = useCallback((e) => {
        if (!isPressed.current || isDragged.current) return;
        if (!startPos.current) return;

        const dx = e.clientX - startPos.current.x;
        const dy = e.clientY - startPos.current.y;
        if (dx * dx + dy * dy > 64) {
            // Drag detected — cancel long-press and release capture so the container can take over
            isDragged.current = true;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            try {
                e.target.releasePointerCapture(e.pointerId);
            } catch {
                /* already released */
            }
        }
    }, []);

    const handlePointerUp = useCallback(
        (e) => {
            if (!isPressed.current) return;

            e.preventDefault();
            const wasDragged = isDragged.current;
            const wasLongPress = isLongPress.current;
            cleanup();

            // Only trigger click if it wasn't a long press or drag
            if (!wasLongPress && !wasDragged) {
                onClick();
            }
        },
        [onClick, cleanup]
    );

    const handlePointerCancel = useCallback(() => {
        cleanup();
    }, [cleanup]);

    // Check if this button's value matches (positive or negative)
    const isPositiveSelected = currentValue === val;
    const isNegativeSelected = currentValue === -val;
    const isSelected = isPositiveSelected || isNegativeSelected;

    return (
        <Button
            data-drag
            data-tooth={toothNum}
            data-loc={row}
            data-field="recession"
            data-value={val}
            size="small"
            variant={isSelected ? "contained" : "outlined"}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onPointerLeave={handlePointerCancel}
            // Prevent context menu on long press (mobile)
            onContextMenu={(e) => e.preventDefault()}
            disabled={disabled}
            sx={{
                width: isLargeScreen ? "100%" : "auto",
                minWidth: isLargeScreen ? "auto" : 20,
                height: isLargeScreen ? 22 : 20,
                p: 0.4,
                fontSize: "0.85rem",
                lineHeight: "0.80rem",
                // Prevent 300ms delay and disable touch gestures on this element
                touchAction: "manipulation",
                // Prevent text selection on long press
                userSelect: "none",
                WebkitUserSelect: "none",
                bgcolor: isSelected ? (isNegativeSelected ? "#7b1fa2" : "background.button.gray") : "background.primary",
                color: isSelected ? "background.primary" : "background.button.gray",
                "&:hover": {
                    bgcolor: isSelected ? (isNegativeSelected ? "#6a1b9a" : "#555") : "#eeeeee"
                },
                "&.Mui-disabled": {
                    bgcolor: isSelected ? (isNegativeSelected ? "#7b1fa2" : "background.button.gray") : "background.primary",
                    color: isSelected ? "background.primary" : "background.button.gray",
                    opacity: 0.85
                }
            }}>
            {isNegativeSelected ? `-${val}` : val}
        </Button>
    );
};

// Memoized ToothRow component to prevent unnecessary re-renders
// Only re-renders when this specific tooth's data changes

const decideCellBackgroundColor = (toothData, index, color) => {
    const isMissing = toothData?.missing;
    if (isMissing) return "background.secondary";

    return index % 2 === 0 ? `background.${color}-a` : `background.${color}-b`;
};

export const MemoizedToothRow = React.memo(
    ({
        toothNum,
        toothData,
        index,
        furcationConfig,
        currentRows,
        currentLabels,
        supPlaqueLabels,
        probingDepthValues,
        recessionValues,
        furcationValues,
        mobilityValues,
        isMobile,
        isLargeScreen,
        visibleColumns,
        chartSeparately,
        readOnly,
        handleUpdate,
        handleToggle,
        handleMobilityUpdate,
        handleToggleAllBleeding
    }) => {
        return (
            <TableRow key={toothNum}>
                {/* Tooth Number Column - Always visible on mobile */}
                {isMobile && (
                    <TableCell
                        sx={{
                            bgcolor: "background.secondary",
                            border: "1px solid #ddd",
                            p: 0.4,
                            textAlign: "center",
                            fontWeight: "bold",
                            fontSize: "0.9rem"
                        }}>
                        {toothNum}
                    </TableCell>
                )}

                {/* Bleeding Sites Column */}
                {(!isMobile || visibleColumns.bleeding) && (
                    <TableCell sx={{ bgcolor: decideCellBackgroundColor(toothData, index, "red"), border: "1px solid #ddd", p: 0 }}>
                        <Box sx={{ display: "flex", height: "100%" }}>
                            {/* Left column: Tooth number button spanning all rows */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", borderRight: "1px solid #ddd", px: 0.25 }}>
                                <Button
                                    variant={
                                        currentRows.every((row) => toothData[row].bleeding)
                                            ? "contained"
                                            : currentRows.some((row) => toothData[row].bleeding)
                                              ? "outlined"
                                              : "outlined"
                                    }
                                    onClick={() => !readOnly && !toothData?.missing && handleToggleAllBleeding(toothNum, currentRows)}
                                    disabled={readOnly || !!toothData?.missing}
                                    sx={{
                                        minWidth: 24,
                                        height: 50,
                                        p: 0.4,
                                        fontWeight: "bold",
                                        fontSize: "0.9rem",
                                        bgcolor: currentRows.every((row) => toothData[row].bleeding) ? "background.button.red" : "background.primary",
                                        color: currentRows.every((row) => toothData[row].bleeding) ? "background.primary" : "background.button.red",
                                        borderColor: "background.button.red",
                                        "&:hover": {
                                            bgcolor: currentRows.every((row) => toothData[row].bleeding) ? "#c62828" : "#ffebee",
                                            borderColor: "background.button.red"
                                        },
                                        "&.Mui-disabled": {
                                            bgcolor: currentRows.every((row) => toothData[row].bleeding) ? "background.button.red" : "background.primary",
                                            color: currentRows.every((row) => toothData[row].bleeding) ? "background.primary" : "background.button.red",
                                            borderColor: "background.button.red",
                                            opacity: 0.85
                                        }
                                    }}>
                                    {toothNum}
                                </Button>
                            </Box>

                            {/* Right column: Individual bleeding site buttons */}
                            <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                {currentRows.map((row, idx) => (
                                    <Box
                                        key={row}
                                        sx={{
                                            borderBottom: idx < 2 ? "1px solid #ddd" : "none",
                                            p: 0.15,
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            flex: 1
                                        }}>
                                        <Button
                                            variant={toothData[row].bleeding ? "contained" : "outlined"}
                                            onClick={() => !readOnly && !toothData?.missing && handleToggle(toothNum, row, "bleeding")}
                                            disabled={readOnly || !!toothData?.missing}
                                            sx={{
                                                width: isLargeScreen ? "100%" : "auto",
                                                minWidth: isLargeScreen ? "auto" : 45,
                                                height: isLargeScreen ? 22 : 20,
                                                p: 0.4,
                                                fontSize: "0.8rem",
                                                lineHeight: "0.65rem",
                                                textTransform: "none",
                                                bgcolor: toothData[row].bleeding ? "background.button.red" : "background.primary",
                                                color: toothData[row].bleeding ? "background.primary" : "background.button.red",
                                                borderColor: "background.button.red",
                                                "&:hover": {
                                                    bgcolor: toothData[row].bleeding ? "#c62828" : "#ffebee",
                                                    borderColor: "background.button.red"
                                                },
                                                "&.Mui-disabled": {
                                                    bgcolor: toothData[row].bleeding ? "background.button.red" : "background.primary",
                                                    color: toothData[row].bleeding ? "background.primary" : "background.button.red",
                                                    borderColor: "background.button.red",
                                                    opacity: 0.85
                                                }
                                            }}>
                                            {currentLabels[idx]}
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </TableCell>
                )}

                {/* Probing Depth Column */}
                {(!isMobile || visibleColumns.probing) && (
                    <TableCell sx={{ bgcolor: decideCellBackgroundColor(toothData, index, "lightblue"), border: "1px solid #ddd", p: 0 }}>
                        {currentRows.map((row, idx) => (
                            <Box
                                key={row}
                                sx={{
                                    display: "flex",
                                    gap: 0.2,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: 24,
                                    borderBottom: idx < 2 ? "1px solid #ddd" : "none",
                                    px: 0.5
                                }}>
                                {probingDepthValues.map((val) => (
                                    <Button
                                        key={val}
                                        data-drag
                                        data-tooth={toothNum}
                                        data-loc={row}
                                        data-field="probing_depth"
                                        data-value={val}
                                        size="small"
                                        variant={toothData[row].probing_depth === val ? "contained" : "outlined"}
                                        onClick={() => !readOnly && !toothData?.missing && handleUpdate(toothNum, row, "probing_depth", val)}
                                        disabled={readOnly || !!toothData?.missing}
                                        sx={{
                                            width: isLargeScreen ? "100%" : "auto",
                                            minWidth: isLargeScreen ? "auto" : 20,
                                            height: isLargeScreen ? 22 : 20,
                                            p: 0.4,
                                            fontSize: "0.85rem",
                                            lineHeight: "0.80rem",
                                            bgcolor: toothData[row].probing_depth === val ? "background.button.blue" : "background.primary",
                                            color: toothData[row].probing_depth === val ? "background.primary" : "background.button.blue",
                                            "&:hover": {
                                                bgcolor: toothData[row].probing_depth === val ? "#1565c0" : "background.secondary"
                                            },
                                            "&.Mui-disabled": {
                                                bgcolor: toothData[row].probing_depth === val ? "background.button.blue" : "background.primary",
                                                color: toothData[row].probing_depth === val ? "background.primary" : "background.button.blue",
                                                opacity: 0.85
                                            }
                                        }}>
                                        {val}
                                    </Button>
                                ))}
                            </Box>
                        ))}
                    </TableCell>
                )}

                {/* Recession Column - Long press for negative values (gingival overgrowth) */}
                {(!isMobile || visibleColumns.recession) && (
                    <TableCell sx={{ bgcolor: decideCellBackgroundColor(toothData, index, "gray"), border: "1px solid #ddd", p: 0 }}>
                        {currentRows.map((row, idx) => (
                            <Box
                                key={row}
                                sx={{
                                    display: "flex",
                                    gap: 0.2,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: 24,
                                    borderBottom: idx < 2 ? "1px solid #ddd" : "none",
                                    px: 0.5
                                }}>
                                {recessionValues.map((val) => (
                                    <RecessionButton
                                        key={val}
                                        val={val}
                                        currentValue={toothData[row].recession}
                                        onClick={() => handleUpdate(toothNum, row, "recession", val)}
                                        onLongPress={() => handleUpdate(toothNum, row, "recession", -val)}
                                        disabled={readOnly || !!toothData?.missing}
                                        isLargeScreen={isLargeScreen}
                                        toothNum={toothNum}
                                        row={row}
                                    />
                                ))}
                            </Box>
                        ))}
                    </TableCell>
                )}

                {/* Furcation Class Column */}
                {(!isMobile || visibleColumns.furcation) && (
                    <TableCell sx={{ bgcolor: decideCellBackgroundColor(toothData, index, "blue"), border: "1px solid #ddd", p: 0 }}>
                        {furcationConfig.show ? (
                            furcationConfig.rows.map((row, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: "flex",
                                        gap: 0.5,
                                        alignItems: "center",
                                        justifyContent: furcationConfig.isEmpty[idx] ? "center" : "right",
                                        height: 24,
                                        borderBottom: idx < furcationConfig.rows.length - 1 ? "1px solid #ddd" : "none",
                                        px: 0.5
                                    }}>
                                    {!furcationConfig.isEmpty[idx] && (
                                        <>
                                            <Typography sx={{ lineHeight: "0.80rem", minWidth: 30, textAlign: "right", mr: 0.5 }} style={{ fontSize: "0.7rem" }}>
                                                {furcationConfig.labels[idx]}
                                            </Typography>

                                            {furcationValues.map((val) => (
                                                <Button
                                                    key={val}
                                                    size="small"
                                                    variant={toothData[row].furcation === val ? "contained" : "outlined"}
                                                    onClick={() => !readOnly && !toothData?.missing && handleUpdate(toothNum, row, "furcation", val)}
                                                    disabled={readOnly || !!toothData?.missing}
                                                    sx={{
                                                        width: isLargeScreen ? "100%" : "auto",
                                                        minWidth: isLargeScreen ? "auto" : 20,
                                                        height: isLargeScreen ? 22 : 20,
                                                        p: 0.4,
                                                        fontSize: "0.85rem",
                                                        lineHeight: "0.80rem",
                                                        bgcolor: toothData[row].furcation === val ? "background.button.blue" : "background.primary",
                                                        color: toothData[row].furcation === val ? "background.primary" : "background.button.blue",
                                                        "&:hover": {
                                                            bgcolor: toothData[row].furcation === val ? "#1565c0" : "background.secondary"
                                                        },
                                                        "&.Mui-disabled": {
                                                            bgcolor: toothData[row].furcation === val ? "background.button.blue" : "background.primary",
                                                            color: toothData[row].furcation === val ? "background.primary" : "background.button.blue",
                                                            opacity: 0.85
                                                        }
                                                    }}>
                                                    {val}
                                                </Button>
                                            ))}
                                        </>
                                    )}
                                </Box>
                            ))
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 76 }}></Box>
                        )}
                    </TableCell>
                )}

                {/* SUP Column */}
                {(!isMobile || visibleColumns.sup) && (
                    <TableCell sx={{ bgcolor: decideCellBackgroundColor(toothData, index, "orange"), border: "1px solid #ddd", p: 0 }}>
                        {supPlaqueLabels.map((label, idx) => {
                            const row = currentRows[idx];
                            return (
                                <Box
                                    key={label}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        height: 24,
                                        borderBottom: idx < 2 ? "1px solid #ddd" : "none"
                                    }}>
                                    <Button
                                        size="small"
                                        variant={toothData[row].sup ? "contained" : "outlined"}
                                        onClick={() => !readOnly && !toothData?.missing && handleToggle(toothNum, row, "sup")}
                                        disabled={readOnly || !!toothData?.missing}
                                        sx={{
                                            width: isLargeScreen ? "100%" : "auto",
                                            minWidth: isLargeScreen ? "auto" : 45,
                                            height: isLargeScreen ? 22 : 20,
                                            p: 0.4,
                                            fontSize: "0.85rem",
                                            lineHeight: "0.80rem",
                                            textTransform: "none",
                                            bgcolor: toothData[row].sup ? "background.button.orange" : "background.primary",
                                            color: toothData[row].sup ? "background.primary" : "background.button.orange",
                                            borderColor: "background.button.orange",
                                            "&:hover": {
                                                bgcolor: toothData[row].sup ? "#ef6c00" : "#fff3e0",
                                                borderColor: "background.button.orange"
                                            },
                                            "&.Mui-disabled": {
                                                bgcolor: toothData[row].sup ? "background.button.orange" : "background.primary",
                                                color: toothData[row].sup ? "background.primary" : "background.button.orange",
                                                borderColor: "background.button.orange",
                                                opacity: 0.85
                                            }
                                        }}>
                                        {label}
                                    </Button>
                                </Box>
                            );
                        })}
                    </TableCell>
                )}

                {/* Plaque Column */}
                {!chartSeparately && (!isMobile || visibleColumns.plaque) && (
                    <TableCell sx={{ bgcolor: decideCellBackgroundColor(toothData, index, "purple"), border: "1px solid #ddd", p: 0 }}>
                        {supPlaqueLabels.map((label, idx) => {
                            const row = currentRows[idx];
                            return (
                                <Box
                                    key={label}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        height: 24,
                                        borderBottom: idx < 2 ? "1px solid #ddd" : "none"
                                    }}>
                                    <Button
                                        size="small"
                                        variant={toothData[row].plaque ? "contained" : "outlined"}
                                        onClick={() => !readOnly && !toothData?.missing && handleToggle(toothNum, row, "plaque")}
                                        disabled={readOnly || !!toothData?.missing}
                                        sx={{
                                            width: isLargeScreen ? "100%" : "auto",
                                            minWidth: isLargeScreen ? "auto" : 45,
                                            height: isLargeScreen ? 22 : 20,
                                            p: 0.4,
                                            fontSize: "0.85rem",
                                            lineHeight: "0.80rem",
                                            textTransform: "none",
                                            bgcolor: toothData[row].plaque ? "background.button.purple" : "background.primary",
                                            color: toothData[row].plaque ? "background.primary" : "background.button.purple",
                                            borderColor: "background.button.purple",
                                            "&:hover": {
                                                bgcolor: toothData[row].plaque ? "#6a1b9a" : "#f3e5f5",
                                                borderColor: "background.button.purple"
                                            },
                                            "&.Mui-disabled": {
                                                bgcolor: toothData[row].plaque ? "background.button.purple" : "background.primary",
                                                color: toothData[row].plaque ? "background.primary" : "background.button.purple",
                                                borderColor: "background.button.purple",
                                                opacity: 0.85
                                            }
                                        }}>
                                        {label}
                                    </Button>
                                </Box>
                            );
                        })}
                    </TableCell>
                )}

                {/* Mobility Column */}
                {(!isMobile || visibleColumns.mobility) && (
                    <TableCell sx={{ bgcolor: decideCellBackgroundColor(toothData, index, "green"), border: "1px solid #ddd", p: 0.5 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                            {mobilityValues.map((val) => (
                                <Button
                                    key={val}
                                    size="small"
                                    variant={toothData.mobility === val ? "contained" : "outlined"}
                                    onClick={() => !readOnly && !toothData?.missing && handleMobilityUpdate(toothNum, val)}
                                    disabled={readOnly || !!toothData?.missing}
                                    sx={{
                                        width: isLargeScreen ? "100%" : "auto",
                                        minWidth: isLargeScreen ? "auto" : 45,
                                        height: isLargeScreen ? 22 : 20,
                                        p: 0.4,
                                        fontSize: "0.85rem",
                                        lineHeight: "0.80rem",
                                        bgcolor: toothData.mobility === val ? "background.button.green" : "background.primary",
                                        color: toothData.mobility === val ? "background.primary" : "background.button.green",
                                        borderColor: "background.button.green",
                                        "&:hover": {
                                            bgcolor: toothData.mobility === val ? "#558b2f" : "#f1f8e9",
                                            borderColor: "background.button.green"
                                        },
                                        "&.Mui-disabled": {
                                            bgcolor: toothData.mobility === val ? "background.button.green" : "background.primary",
                                            color: toothData.mobility === val ? "background.primary" : "background.button.green",
                                            borderColor: "background.button.green",
                                            opacity: 0.85
                                        }
                                    }}>
                                    {val}
                                </Button>
                            ))}
                        </Box>
                    </TableCell>
                )}
            </TableRow>
        );
    },
    (prevProps, nextProps) => {
        // Fast path: check simple props first
        if (
            prevProps.toothNum !== nextProps.toothNum ||
            prevProps.index !== nextProps.index ||
            prevProps.readOnly !== nextProps.readOnly ||
            prevProps.chartSeparately !== nextProps.chartSeparately ||
            prevProps.isMobile !== nextProps.isMobile ||
            prevProps.isLargeScreen !== nextProps.isLargeScreen
        ) {
            return false;
        }

        // Check visibleColumns (shallow comparison of object values)
        const prevVis = prevProps.visibleColumns;
        const nextVis = nextProps.visibleColumns;
        if (
            prevVis.bleeding !== nextVis.bleeding ||
            prevVis.probing !== nextVis.probing ||
            prevVis.recession !== nextVis.recession ||
            prevVis.furcation !== nextVis.furcation ||
            prevVis.sup !== nextVis.sup ||
            prevVis.plaque !== nextVis.plaque ||
            prevVis.mobility !== nextVis.mobility
        ) {
            return false;
        }

        // Check toothData - fast path if same reference
        const prevTooth = prevProps.toothData;
        const nextTooth = nextProps.toothData;
        if (prevTooth === nextTooth) {
            return true;
        }

        // Check tooth-level properties
        if (prevTooth.missing !== nextTooth.missing || prevTooth.mobility !== nextTooth.mobility || prevTooth.implant !== nextTooth.implant) {
            return false;
        }

        // Check each location - only compare if references differ
        const locations = ["db", "b", "mb", "dp", "p", "mp", "dl", "l", "ml"];
        for (const loc of locations) {
            const prevLoc = prevTooth[loc];
            const nextLoc = nextTooth[loc];

            // Fast path: same reference means no change
            if (prevLoc === nextLoc) continue;

            // Compare individual fields
            if (
                prevLoc?.bleeding !== nextLoc?.bleeding ||
                prevLoc?.probing_depth !== nextLoc?.probing_depth ||
                prevLoc?.recession !== nextLoc?.recession ||
                prevLoc?.furcation !== nextLoc?.furcation ||
                prevLoc?.sup !== nextLoc?.sup ||
                prevLoc?.plaque !== nextLoc?.plaque
            ) {
                return false;
            }
        }

        return true;
    }
);
